// Загрузка общих данных: конфиг, тренды, пользователь, мои генерации.
import { ApiError, api, errorText } from './api';
import { redeemAuthReturn } from './authReturn';
import { sendAdTag } from './ads';
import { clearToken, launchLinkNonce, platform } from './platform';
import { getState, openSheet, setState, toast } from './store';
import type { Generation } from './types';
import { applySkin, getSkin, isTenant, loadPreviewSkin } from './wl/skin';

let linkAsked = false;

let bootStarted = false;

/** Первый запуск загрузки данных — один раз: из main.tsx сразу после хоста (параллельно со стилями) или из App. */
export function startBoot(): void {
  if (bootStarted) return;
  bootStarted = true;
  void bootData();
}

export async function bootData(): Promise<void> {
  setState({ boot: 'loading', bootError: null });
  // вернулись с Яндекс ID или VK ID (или по кнопке из чата с ботом) — сначала обменять одноразовый код на сессию
  const late = await redeemAuthReturn();
  void refreshMe().then(() => {
    // код сгорел, а вошедшим человек так и не оказался — теперь сказать, что вход устарел
    const me = getState().me;
    if (late && (!me || me.user.guest)) toast(late, 'error', 6000);
  });
  try {
    // предпросмотр сайта проекта White Label на нашем адресе (?wl=<slug>) — оформление до первой отрисовки экранов
    await loadPreviewSkin(api.wlSkin);
    const [config, trends] = await Promise.all([api.getConfig(), api.getTrends()]);
    // сайт проекта, а оформление не пришло со страницей (у себя: vite отдаёт index.html сам) — берём из настроек
    if (config.wl && !getSkin()) applySkin(config.wl);
    setState({ config, trends, boot: 'ready' });
    // мини-приложение VK открыли с сайта, чтобы связать аккаунты, — спрашиваем подтверждение
    const nonce = platform.kind === 'vk' && !linkAsked ? launchLinkNonce() : null;
    if (nonce) {
      linkAsked = true;
      openSheet({ kind: 'link-confirm', nonce });
    }
  } catch (e) {
    setState({ boot: 'error', bootError: errorText(e) });
  }
}

let meInFlight: Promise<void> | null = null;

/** Обновить пользователя и образы. В вебе без токена — гость. */
export function refreshMe(): Promise<void> {
  if (meInFlight) return meInFlight;
  meInFlight = (async () => {
    if (!platform.authHeader()) {
      setState({ auth: 'guest', me: null, authError: null, generations: null });
      return;
    }
    try {
      const me = await api.getMe();
      setState({ me, auth: 'user', authError: null });
      void sendAdTag();
      // тестовая оплата открыта только админам — после входа админа настройки нужно спросить заново
      if (me.user.isAdmin && getState().config && !getState().config?.paymentsEnabled) {
        void api.getConfig().then((config) => setState({ config })).catch(() => undefined);
      }
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        if (platform.kind === 'web') {
          clearToken();
          setState({ auth: 'guest', me: null, authError: null, generations: null });
        } else {
          setState({ auth: 'error', authError: errorText(e) });
        }
      } else if (getState().auth !== 'user') {
        setState({ auth: 'error', authError: errorText(e) });
      }
    }
  })().finally(() => {
    meInFlight = null;
  });
  return meInFlight;
}

export async function refreshGenerations(): Promise<Generation[] | null> {
  if (getState().auth !== 'user') return null;
  try {
    const list = await api.listGenerations();
    setState({ generations: list });
    return list;
  } catch {
    return null;
  }
}

/** Ссылка на тренд для друзей — в приложение той же платформы; на сайте — с кодом приглашения вошедшего (доля — ему). */
export function trendShareLink(trendId: string): string {
  const { config: cfg, me } = getState();
  const code = me && !me.user.guest && !isTenant() ? me.user.refCode : null;
  // ролик Студии ведёт друзей в Студию — на сайт (в мини-приложениях своего адреса у неё нет)
  if (trendId === 'studio') return `${(cfg?.webUrl || window.location.origin).replace(/\/$/, '')}/studio${code ? `?r=${encodeURIComponent(code)}` : ''}`;
  const web = `${(cfg?.webUrl || window.location.origin).replace(/\/$/, '')}/t/${encodeURIComponent(trendId)}/make${code ? `?r=${encodeURIComponent(code)}` : ''}`;
  if (platform.kind === 'tg' && cfg?.botUsername) return `https://t.me/${cfg.botUsername}?start=t_${trendId}`;
  if (platform.kind === 'vk' && cfg?.vkAppId) return `https://vk.com/app${cfg.vkAppId}#t_${trendId}`;
  return web;
}
